// Cards y heroes del browse de la home V2. Dos componentes
// presentacionales:
//
//   • MobileModeHero — carousel swipeable mobile-only que reemplaza
//     la fila de cards "Diseña tu imagen" en mobile. Slides verticales
//     (aspect 2:3) con video/imagen + título overlay + botón Generar
//     per-slide y dots indicadores. Auto-advance cada 9s (pausa al
//     tocar y al esconderse la pestaña).
//
//   • PresetCard — card genérica de preset usada en TODAS las filas
//     de la home (desktop y mobile). Tiene dos layouts internos según
//     `preset.kind`:
//      - design-modelo/design-auto/catalogo-ia/asistente → carátula
//        grande (3:2 desktop · 4:5 mobile) con título + descripción
//        sueltos debajo de la imagen (sin card chrome).
//      - resto → tile 3:4 con imagen full-bleed + título overlay
//        gradient.

import React, { useEffect, useRef, useState } from 'react';
import { ChevronRight, Play, Lock } from 'lucide-react';
import type { CollectionV2, PresetV2 } from '../mockData';
import { HoverScrollCarousel } from '../HoverScrollCarousel';
import {
  MODE_KIND_TO_PATH,
  PORTADA_CUADRADA,
  portadaCuadradaDeRubro,
  nombreCortoModo,
  caratulaModo,
} from '../modesCatalog';
// Los nombres de los modos viven en `COPY_TIRA`, dentro del shell, y se exponen por
// esta función — el mismo camino que usan `AsistenteMode` y `PresetDetailSheet`. No
// hay ciclo: `EstiloMinimal` no importa este archivo.
// Las carátulas del carrusel: la MISMA tabla que usa la tira del inicio en desktop
// (ADR-2026-08-04-el-carrusel-de-mobile-usa-las-caratulas-de-desktop). Tampoco hay ciclo
// acá: `contenidoAtlas` sólo lee `MODES`.

// Cuántos presets se muestran por colección. En desktop entran más
// thumbnails porque hay más espacio horizontal. Si una colección tiene
// menos o igual que el count del device, NO se muestra "Ver más"
// (renderizamos todos los presets y no tiene sentido el link).
const PRESETS_PREVIEW_COUNT_MOBILE = 6;
const PRESETS_PREVIEW_COUNT_DESKTOP = 10;

// iOS (iPhone y iPad) bloquea el autoplay si el <video> no está muteado
// como ATRIBUTO en el momento del play. React solo setea la propiedad
// `muted`, no el atributo, así que en iPad las carátulas no arrancaban.
// Este ref fuerza muted/playsinline como atributo y dispara play() (con
// reintento en `canplay` porque usamos preload=metadata). Se usa en todos
// los <video> de autoplay de la home.
// `prefers-reduced-motion: reduce` → las carátulas en video se quedan en su poster
// (ADR-2026-09-24-las-caratulas-en-video-respetan-reduced-motion). Cuando eran todas
// imágenes esto no existía; ahora hay clips que loopean solos hasta 18s, que es justo
// lo que la preferencia pide no mostrar.
const QUIETO =
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : null;

// Un `<video>` marcado con este atributo es una carátula manejada por
// `setupAutoplayVideo`, y nada más lo usa: es lo que deja encontrarlas a todas desde
// el listener de abajo sin tener que guardarlas en una lista.
const MARCA_CARATULA = 'data-caratula-autoplay';

// Lo que el IntersectionObserver de cada carátula vio la última vez. El listener de
// abajo necesita saber cuáles se están viendo, y preguntárselo a la geometría no sirve:
// en las filas que scrollean de costado —el carrusel de presets, el hero de mobile— una
// card corrida queda fuera de la vista con su rect todavía dentro del alto de la
// ventana. Peor: como su estado de intersección NO cambió, el observer tampoco la
// volvería a pausar, así que arrancaba y seguía sonando invisible
// (ADR-2026-09-24-el-listener-le-pregunta-al-observer).
const MARCA_VISIBLE = 'data-caratula-visible';

// La preferencia se puede prender con la pantalla ABIERTA, y entonces el clip que ya
// estaba a la vista sigue girando — ADR-2026-09-24-reduced-motion-tambien-a-mitad-de-pantalla,
// que supersede el "se lee sólo al montar" de la entrada anterior.
//
// Es UN listener para toda la app, registrado al cargar el módulo, y al disparar busca
// las carátulas en el DOM en vez de cerrar sobre ellas. Esa es la diferencia que
// importa: en React 18 un ref callback no puede devolver limpieza, así que un listener
// POR video no se removería nunca y cada card desmontada quedaría retenida por su
// closure. Éste no referencia ningún elemento y no hay nada que limpiar.
QUIETO?.addEventListener('change', () => {
  const caratulas = document.querySelectorAll<HTMLVideoElement>(`video[${MARCA_CARATULA}]`);
  for (const el of caratulas) {
    if (QUIETO.matches) {
      el.autoplay = false;
      el.removeAttribute('autoplay');
      if (!el.paused) el.pause();
    } else if (el.hasAttribute(MARCA_VISIBLE)) {
      // Al apagar la preferencia sólo arrancan las que el observer dio por visibles; las
      // demás las despierta su propio observer cuando entren, como siempre.
      const p = el.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    }
  }
});

export const setupAutoplayVideo = (el: HTMLVideoElement | null) => {
  if (!el) return;
  el.muted = true;
  el.defaultMuted = true;
  el.setAttribute('muted', '');
  el.setAttribute('playsinline', '');
  el.setAttribute(MARCA_CARATULA, '');
  // No alcanza con NO llamar a play(): el atributo `autoPlay` del JSX hace que el
  // navegador arranque igual, así que hay que sacarlo acá, en el ref, antes de que
  // el clip tenga datos para reproducir.
  if (QUIETO?.matches) {
    el.autoplay = false;
    el.removeAttribute('autoplay');
  }
  const tryPlay = () => {
    if (QUIETO?.matches) {
      if (!el.paused) el.pause();
      return;
    }
    const p = el.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  };
  // Pausar el video cuando NO está visible (fuera de pantalla, o su contenedor
  // quedó `display:none` — ej. la home detrás de un panel/sub-modo abierto).
  // Un video display:none igual sigue decodificando frames y gasta GPU/CPU;
  // pausarlo libera ese costo. Vuelve a reproducir al verse de nuevo.
  if (typeof IntersectionObserver !== 'undefined') {
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          // El veredicto del observer queda anotado en el elemento: es lo que lee el
          // listener de la preferencia para saber a cuáles hacer arrancar.
          if (e.isIntersecting) {
            el.setAttribute(MARCA_VISIBLE, '');
            tryPlay();
          } else {
            el.removeAttribute(MARCA_VISIBLE);
            if (!el.paused) el.pause();
          }
        }
      },
      { threshold: 0.01 },
    );
    io.observe(el);
  } else {
    // Sin IntersectionObserver no hay a quién preguntarle: acá se reproduce siempre,
    // así que la carátula cuenta como visible.
    el.setAttribute(MARCA_VISIBLE, '');
    tryPlay();
    el.addEventListener('canplay', tryPlay, { once: true });
  }
};

// Overlay de "modo bloqueado" (plan insuficiente): oscurece la carátula y muestra
// un candado en un círculo oscuro traslúcido. Se reusa en las cards de modo de la
// home (mobile hero + desktop), en el sheet de Generar y en la página Modos.
// `pointer-events-none` para que el click igual llegue a la card/botón de abajo
// (que dispara el modal de "función exclusiva").
//
// El círculo era `bg-brand-primary` — ADR-2026-08-04-el-candado-usa-un-solo-color.
// El azul de marca es el color de lo que se PUEDE hacer (los CTA, el ítem activo de
// la sidebar) y acá anunciaba lo contrario; encima la tira de modos del shell nuevo
// ya dibujaba el suyo en `bg-black/55` con desenfoque, así que el mismo candado se
// veía de dos colores según la pantalla. Éstos son los valores de esa tira.
export const ModeLockOverlay: React.FC = () => (
  <div className="absolute inset-0 z-[2] flex items-center justify-center bg-black/45 pointer-events-none">
    <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-sm">
      <Lock className="h-7 w-7 text-white" strokeWidth={2} />
    </span>
  </div>
);

export interface MobileModeHeroProps {
  cards: PresetV2[];
  onCreate: (preset: PresetV2) => void;
  /** Devuelve true si el modo está bloqueado para el plan del usuario → muestra
   *  el candado sobre la carátula. */
  isLocked?: (card: PresetV2) => boolean;
  /**
   * Rubro de la marca activa — decide si algún modo se muestra con una carátula
   * propia del rubro (ADR-2026-08-05-la-caratula-de-catalogo-sigue-al-rubro).
   */
  rubro?: string | null;
}

export const MobileModeHero: React.FC<MobileModeHeroProps> = ({
  cards,
  onCreate,
  isLocked,
  rubro,
}) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [activeIdx, setActiveIdx] = useState(0);
  // `touching` pausa el auto-advance mientras el dedo está apoyado en
  // el carousel — sin esto el browser pelearía con nuestro `scrollTo`
  // a mitad de un swipe.
  const [touching, setTouching] = useState(false);
  // Contador para re-disparar el effect de auto-advance cuando la
  // pestaña vuelve a estar visible (sin tocar activeIdx).
  const [autoTick, setAutoTick] = useState(0);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el || el.clientWidth <= 0) return;
    const idx = Math.round(el.scrollLeft / el.clientWidth);
    if (idx !== activeIdx && idx >= 0 && idx < cards.length) {
      setActiveIdx(idx);
    }
  };

  // Auto-advance: cada 9 segundos pasamos al siguiente slide en loop.
  // El timer se resetea cuando `activeIdx` cambia (sea por la propia
  // automatización o por un swipe manual), así el user siempre tiene
  // ese intervalo completo de "lectura" antes de que vuelva a pasar.
  // Se pausa con el dedo apoyado o cuando la pestaña no está visible.
  useEffect(() => {
    if (cards.length < 2) return;
    if (touching) return;
    if (typeof document !== 'undefined' && document.hidden) return;
    const id = window.setTimeout(() => {
      const el = scrollRef.current;
      if (!el || el.clientWidth <= 0) return;
      const next = (activeIdx + 1) % cards.length;
      el.scrollTo({ left: next * el.clientWidth, behavior: 'smooth' });
    }, 9000);
    return () => window.clearTimeout(id);
  }, [activeIdx, cards.length, touching, autoTick]);

  // Re-arma el timer cuando la pestaña vuelve a estar visible — sin
  // esto, si el user salió y volvió, el carousel quedaba parado.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onVisibility = () => {
      if (!document.hidden) setAutoTick((t) => t + 1);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () =>
      document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // Lock direccional del swipe. Con `touch-action: pan-y` (en el style) el
  // browser se reserva SOLO el scroll vertical de la página — que sigue
  // funcionando aunque el dedo arranque sobre los videos — y nos deja el eje
  // X a nosotros. Al primer movimiento significativo fijamos el eje del gesto:
  //   • horizontal → movemos el carousel a mano y `preventDefault()` para
  //     matar cualquier scroll vertical nativo (sin esto aparecía el "drift"
  //     diagonal en Y). Al soltar, snap suave al slide más cercano.
  //   • vertical   → no tocamos nada: la página scrollea nativo y el eje X
  //     queda bloqueado por `pan-y`, así que tampoco hay drift horizontal.
  // (touchmove va como listener NO pasivo vía addEventListener porque el
  //  onTouchMove de React es pasivo y ahí `preventDefault` no tiene efecto.)
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let startX = 0;
    let startY = 0;
    let startLeft = 0;
    let startTime = 0;
    let axis: 'x' | 'y' | null = null;
    const THRESH = 6;

    const onStart = (e: TouchEvent) => {
      const t = e.touches[0];
      startX = t.clientX;
      startY = t.clientY;
      startLeft = el.scrollLeft;
      startTime = Date.now();
      axis = null;
      setTouching(true);
    };
    const onMove = (e: TouchEvent) => {
      const t = e.touches[0];
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      if (axis === null) {
        if (Math.abs(dx) < THRESH && Math.abs(dy) < THRESH) return;
        axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      }
      if (axis === 'x') {
        e.preventDefault();
        el.scrollLeft = startLeft - dx;
      }
    };
    const onEnd = () => {
      setTouching(false);
      if (axis === 'x' && el.clientWidth > 0) {
        const w = el.clientWidth;
        const startIdx = Math.round(startLeft / w);
        const dragged = el.scrollLeft - startLeft; // + = hacia el siguiente
        const frac = dragged / w;
        const dt = Date.now() - startTime;
        // "Flick": un swipe corto y rápido pasa de slide aunque no llegue al
        // umbral de distancia (se siente natural).
        const flick = Math.abs(dragged) > 24 && dt < 250;
        // Umbral 30% del ancho (antes el snap usaba Math.round = había que
        // cruzar el 50%, y sin flick se sentía ~75% / tosco).
        const THRESHOLD = 0.3;
        let target = startIdx;
        if (frac > THRESHOLD || (flick && dragged > 0)) target = startIdx + 1;
        else if (frac < -THRESHOLD || (flick && dragged < 0)) target = startIdx - 1;
        const maxIdx = Math.max(0, Math.round(el.scrollWidth / w) - 1);
        target = Math.max(0, Math.min(maxIdx, target));
        el.scrollTo({ left: target * w, behavior: 'smooth' });
      }
      axis = null;
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd, { passive: true });
    el.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, []);

  // El hero ocupa todo el ancho de pantalla y empieza desde el tope
  // (sin greeting arriba en mobile). El bottom NO termina abrupto —
  // un overlay con gradient transparente→white sobre la parte
  // inferior del carousel hace que la imagen se diluya en el bg
  // blanco de la página. El botón "Crear" vive dentro de esa zona
  // de degradado, anclado al bottom con `absolute`. `bg-white` en el
  // outer evita una línea de subpixel donde el carousel termina y la
  // página continúa.
  return (
    <div className="relative bg-white">
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex overflow-x-auto scrollbar-hide"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',
          // `pan-y`: el browser sólo maneja el scroll vertical de la página
          // (sigue andando aunque el dedo arranque sobre los videos). El eje
          // X lo movemos a mano con lock direccional (ver el effect de touch
          // arriba), así el swipe horizontal no "driftea" en Y. El snap entre
          // slides también lo hacemos por JS, por eso no va `snap-mandatory`
          // acá (pelearía con el drag manual frame a frame).
          touchAction: 'pan-y',
        }}
      >
        {cards.map((card) => {
          // La carátula sale de `caratulaModo`, la misma fuente que pinta la home de
          // inicio en desktop — ADR-2026-08-04-el-carrusel-de-mobile-usa-las-caratulas-de-desktop.
          //
          // Antes era una cadena de ternarios con los archivos `*_caratula_mobile.*`:
          // cada modo se veía distinto según el dispositivo (Catálogo con video acá y
          // con la grilla allá, Automática con dos videos distintos), y cada carátula
          // nueva había que acordarse de cambiarla en los dos lados.
          //
          // Un modo sin entrada en el catálogo NO se descarta: cae a la imagen del
          // propio preset, igual que hace la tira. Es el mismo criterio con el que
          // `design-modelo` quedó con su portada estática
          // (ADR-2026-08-04-modelo-portada-unica).
          const path = card.kind ? MODE_KIND_TO_PATH[card.kind] : undefined;
          const arte = path ? caratulaModo(path, rubro, card.imageUrl) : undefined;
          // Portada cuadrada, si el modo tiene una
          // (ADR-2026-08-04-la-portada-cuadrada-no-es-de-mobile). Este hero es casi
          // cuadrado (402:375), que es la forma para la que existe esa tabla.
          // La carátula propia del rubro gana sobre la cuadrada y sobre la del
          // catálogo (ADR-2026-08-05-la-caratula-de-catalogo-sigue-al-rubro): si el
          // rubro tiene una, es la que representa al modo para esa marca. Entra por
          // `portadaCuadrada` para reusar su regla de "con portada fija no va el
          // video", que es exactamente lo que se quiere acá.
          const portadaCuadrada = path
            ? (portadaCuadradaDeRubro(path, rubro) ?? PORTADA_CUADRADA[path])
            : undefined;
          // Con portada cuadrada no se reproduce el video: la portada es lo que se
          // quiere ver acá, y como `poster` la taparía el primer frame.
          const videoSrc = portadaCuadrada ? null : (arte?.video ?? null);
          // El póster vacío de Anuncio es a propósito: sin él, `preload="metadata"`
          // dibuja el primer cuadro del propio video, que ES la portada. Por eso
          // `|| undefined` y no `?? undefined`.
          const posterSrc = portadaCuadrada || arte?.poster || undefined;

          const locked = isLocked ? isLocked(card) : false;
          return (
          <div
            key={card.id}
            // `aspect-[402/375]`: la proporción del hero del diseño mobile nuevo
            // (ADR-2026-07-31-home-mobile-hero-nav-sheet-generar) — 375px de alto sobre un ancho de 402.
            // En un iPhone de 390px son ~364px contra los ~487px del `aspect-[4/5]`
            // anterior: el hero deja de ocupar media pantalla y abajo entra el
            // contenido de la home sin tener que scrollear para descubrirlo.
            className="shrink-0 w-screen snap-start relative aspect-[402/375] bg-neutral-100"
          >
            {videoSrc ? (
              <video
                ref={setupAutoplayVideo}
                src={videoSrc}
                poster={posterSrc}
                autoPlay
                loop
                muted
                // `playsInline` para que iOS no abra el video en player
                // fullscreen apenas el user toca el hero.
                playsInline
                // `preload=metadata` para evitar bajar el video entero
                // hasta que el slide entre en viewport (autoplay arranca
                // la descarga real cuando hace falta).
                preload="metadata"
                aria-label={card.title}
                // `pointer-events-none` + `draggable=false`: el gesto (swipe /
                // scroll) pasa directo al contenedor y iOS no inicia su drag
                // nativo de imagen/video (que hacía que el hero "siguiera" al
                // dedo en el primer deslizamiento).
                draggable={false}
                onDragStart={(e) => e.preventDefault()}
                style={{ WebkitTouchCallout: "none" } as React.CSSProperties}
                className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none"
              />
            ) : (
              <img
                // Sin video, la portada del catálogo; y si el modo no está en el
                // catálogo, la imagen del preset — el mismo respaldo que la tira.
                src={posterSrc || card.imageUrl}
                alt={card.title}
                loading="lazy"
                draggable={false}
                onDragStart={(e) => e.preventDefault()}
                style={{ WebkitTouchCallout: "none" } as React.CSSProperties}
                className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none"
              />
            )}
            {locked && <ModeLockOverlay />}
            {/* Velo oscuro de la mitad para abajo — ADR-2026-07-31-home-mobile-hero-nav-sheet-generar.
                Acá había un fade-to-WHITE que diluía la carátula en el fondo de la
                página; el diseño nuevo apoya el texto sobre la imagen, así que el
                degradado ahora oscurece (0 → 60% de negro, arrancando a la mitad,
                que es lo que define el frame de Figma).
                Vive ADENTRO del slide para pintar sobre la imagen y quedar DEBAJO
                del título + botón (z-10): el `overflow` del scroll container crea
                su propio stacking context y un overlay global taparía el botón. */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  'linear-gradient(to bottom, rgba(0,0,0,0) 50%, rgba(0,0,0,0.6) 100%)',
              }}
            />
            {/* Título + botón Generar como UN bloque anclado al fondo del
                slide (`bottom-3`), con un gap fijo (`mb-3` ≈ 12px) entre
                ambos. Antes el título iba a `top:76%` y el botón al fondo por
                separado: el hueco entre ellos crecía con la altura del hero
                (chico en teléfonos comunes, grande en pantallas altas). Al
                agruparlos el espacio título↔botón queda CONSTANTE en todos los
                tamaños. El bloque vive dentro de cada slide para viajar con el
                swipe horizontal; `pointer-events-none` en el contenedor (y
                `-auto` solo en el botón) deja que el swipe pase a través del
                título. Como el título ahora crece hacia ARRIBA (anclado abajo),
                un título de 2 líneas se aleja del botón en vez de pisarlo.
                  • Título en design-auto / anuncio / asistente → blanco
                    (carátulas oscuras); resto → brand-black (carátulas claras).
                `px-1` en el h2 recupera el padding lateral que tenía con `px-6`
                (el contenedor ahora usa `px-5` para alinear con el botón). */}
            <div className="absolute z-10 inset-x-0 bottom-[26px] px-6 pointer-events-none">
              {/* Título SIEMPRE blanco: con el velo oscuro de arriba ya no hace
                  falta elegir el color por carátula (las claras también quedan
                  oscurecidas abajo), y una regla por `kind` era una lista que
                  había que mantener cada vez que entraba un modo. */}
              {/* El nombre sale de `COPY_TIRA` vía `nombreCortoModo`, la MISMA tabla
                  que la tira de desktop, la sidebar, el sheet de Generar y los
                  breadcrumbs (ADR-2026-08-04-nombres-de-modo-iguales-en-mobile).
                  Acá había dos nombres escritos a mano ("Imagen automática",
                  "Imagen personalizada") contra los que decía desktop ("Automática",
                  "Modelo"), y para el resto de los modos caía en `card.title`, que es
                  la frase larga del preset ("Generar catálogo con IA"). */}
              <h2 className="mb-2.5 text-[24px] font-bold leading-[31px] text-white">
                {nombreCortoModo(
                  (card.kind && MODE_KIND_TO_PATH[card.kind]) || '',
                  card.title,
                )}
              </h2>
              {/* Pastilla blanca y al ancho de su texto, no un botón de ancho
                  completo: en el diseño nuevo el hero es una PORTADA que invita a
                  probar el modo, y el botón grande y negro lo leía como el único
                  camino de la pantalla. El CTA de verdad es el botón central de la
                  barra de abajo. Mismo copy que la tira de desktop. */}
              {/* El botón va a la DERECHA y los pasos del carrusel debajo del
                  título, a la izquierda — ADR-2026-08-04-hero-mobile-invierte-pasos-y-boton.
                  Antes era al revés. El título manda a la izquierda: los pasos, que
                  son del mismo bloque de contenido, se le alinean debajo, y la acción
                  queda del lado donde cae el pulgar. */}
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => onCreate(card)}
                  className="pointer-events-auto inline-flex items-center justify-center rounded-md bg-white px-3 py-1.5 text-[12px] font-bold text-[#18181B] shadow-sm active:scale-[0.98] transition-transform"
                >
                  Probar modo
                </button>
              </div>
            </div>
          </div>
          );
        })}
      </div>

      {/* Page indicator — render al final del DOM para que pinte arriba sin
          depender de z-index. Estilos inline para descartar problemas de clases
          Tailwind. El botón vive dentro de cada slide, así que este indicador es
          la única overlay "global".

          ADR-2026-07-31-home-mobile-hero-nav-sheet-generar lo bajó a la altura del CTA
          (mismo `bottom` que la pastilla: 26px + la mitad de sus 29px de alto,
          redondeado), porque arriba a la izquierda pisaba el título.
          ADR-2026-08-04-hero-mobile-invierte-pasos-y-boton lo pasó a la IZQUIERDA:
          los 24px de `left` son los mismos `px-6` del bloque de arriba, así que los
          pasos arrancan justo debajo del título. El CTA se fue a la derecha.

          Se le cayó la lógica de color por carátula: con el velo oscuro del slide
          el fondo bajo los dots es siempre oscuro, así que van siempre claros. */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          bottom: '37px',
          left: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          zIndex: 30,
          pointerEvents: 'none',
        }}
      >
        {cards.map((_, i) => {
          const active = i === activeIdx;
          return (
            <span
              key={i}
              style={{
                display: 'block',
                height: '6px',
                // 41px el activo: el diseño lo dibuja como una barra, no como un
                // punto más grande — se lee de un vistazo en qué slide estás.
                width: active ? '41px' : '12px',
                borderRadius: '9999px',
                backgroundColor: active ? '#FFFFFF' : 'rgba(255, 255, 255, 0.45)',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.25)',
                transition: 'all 200ms ease-out',
              }}
            />
          );
        })}
      </div>
    </div>
  );
};

export interface PresetCardProps {
  preset: PresetV2;
  onClick: () => void;
  /** Modifier opcional (ej: 'hidden md:block' para mostrar solo en desktop). */
  className?: string;
  /** Si true, el modo está bloqueado por plan → carátula oscurecida + candado. */
  locked?: boolean;
}

export const PresetCard: React.FC<PresetCardProps> = ({
  preset,
  onClick,
  className,
  locked,
}) => {
  // Cards de los modos "design" (Imagen con modelo + Modo automático +
  // Catálogo IA): layout de card container con imagen en la parte
  // superior, título + descripción debajo. Los 3 modos comparten layout
  // y ancho (`flex-1 sm:basis-0`) para que se repartan el row equitativa-
  // mente en desktop.
  //   • mobile  → 85% del row con la imagen aspect-[3/4]; al ver una
  //               card entera se asoma un pedacito de la siguiente para
  //               invitar al scroll horizontal.
  //   • desktop → flex-1 con la imagen aspect-[4/5] dentro del card; el
  //               alto total queda definido por la imagen + el bloque
  //               de texto.
  if (
    preset.kind === 'design-modelo' ||
    preset.kind === 'design-auto' ||
    preset.kind === 'fotos-producto' ||
    preset.kind === 'catalogo-ia' ||
    preset.kind === 'catalogo-muebles' ||
    preset.kind === 'infografia' ||
    preset.kind === 'asistente' ||
    preset.kind === 'prompt-libre' ||
    preset.kind === 'creador-video' ||
    preset.kind === 'anuncio' ||
    preset.kind === 'video-influencer' ||
    preset.kind === 'animar-imagen' ||
    preset.kind === 'carrusel'
  ) {
    // Carátula desktop: usamos videos cortos por modo (mp4 comprimido a
    // ~250KB-1.7MB). En mobile esta rama no se renderiza para
    // design-row porque ahí muestra `MobileModeHero` directamente. Para
    // otras filas (templates) que reusan este componente caen al else
    // de abajo con `card.imageUrl`.
    // `design-modelo` no está en esta cadena desde
    // ADR-2026-08-04-modelo-portada-unica, `anuncio` desde
    // ADR-2026-08-04-anuncio-va-con-foto-y-sin-video y `design-auto` desde
    // ADR-2026-08-05-automatica-va-con-foto-y-sin-video: caen al `<img>` con la
    // portada estática del preset, igual que catalogo-muebles e infografía.
    const desktopVideoSrc =
      preset.kind === 'catalogo-ia'
        ? '/images/modo_catalogo_ia/modo_catalogo_caratula_desktop.mp4'
        : // Prompt libre y Creador de video comparten el clip del asistente —la barra
          // de prompt escribiendo— hasta tener uno propio
          // (ADR-2026-09-07-modo-prompt-libre, ADR-2026-09-07-modo-creador-video).
          preset.kind === 'asistente' ||
            preset.kind === 'prompt-libre' ||
            preset.kind === 'creador-video'
          ? '/images/modo_asistente/modo_asistente_caratula_desktop.mp4'
          : preset.kind === 'video-influencer'
            ? '/images/modo_video_ugc/modo_video_ugc_caratula_desktop.mp4'
            : preset.kind === 'animar-imagen'
              ? // PLACEHOLDER: es el clip VERTICAL (4:5) del panel de Animar,
                // así que en el slot 3:2 de desktop se recorta. Falta una
                // landscape propia — ADR-2026-08-14-modo-animar-imagen.
                '/images/video_animar_imagen.mp4'
              : null;
    return (
      <button
        type="button"
        onClick={onClick}
        // Mobile: 85% del row (carousel manual swipe).
        // Desktop: ancho fijo así el row se vuelve scrolleable horizontal
        // — apuntamos a que entren ~3.5 cards en un viewport de ~1500px
        // de contenido (sidebar incluido). En pantallas más anchas se ven
        // 4+; en más angostas se ven 2.5-3. El `shrink-0` impide que el
        // flex container las achique a menos de su ancho declarado.
        className={`shrink-0 w-[85%] text-left group active:scale-[0.99] transition-transform flex flex-col sm:w-[420px] ${className || ''}`}
      >
        {/* Imagen carátula con las 4 esquinas redondeadas. En desktop
            ratio 3:2 (un poco más alta que 16:9), suficiente para que
            la carátula se vea cuadrada-ish sin ocupar demasiado vertical;
            en mobile mantenemos 4:5 porque cada card ocupa el 85% del
            ancho y una ratio panorámica quedaría muy baja. */}
        <div data-carousel-media className="relative aspect-[4/5] sm:aspect-[3/2] bg-neutral-100 rounded-2xl overflow-hidden">
          {desktopVideoSrc ? (
            <video
              ref={setupAutoplayVideo}
              src={desktopVideoSrc}
              // `poster` propio (landscape) si el modo lo define — evita que un
              // imageUrl portrait (ej. la miniatura de video-influencer) flashee
              // recortado en el slot 3:2 antes de que arranque el video.
              poster={preset.poster || preset.imageUrl}
              autoPlay
              loop
              muted
              // `playsInline` para evitar fullscreen player en touch desktops
              // (iPad en modo desktop, etc.).
              playsInline
              preload="metadata"
              aria-label={preset.title}
              // pointer-events-none + draggable=false: el swipe de la fila pasa
              // al scroll y iOS no arranca su drag nativo de imagen/video.
              draggable={false}
              onDragStart={(e) => e.preventDefault()}
              style={{ WebkitTouchCallout: "none" } as React.CSSProperties}
              className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none sm:group-hover:scale-[1.03] transition-transform duration-500 ease-out"
            />
          ) : (
            <img
              src={preset.imageUrl}
              alt={preset.title}
              loading="lazy"
              draggable={false}
              onDragStart={(e) => e.preventDefault()}
              style={{ WebkitTouchCallout: "none" } as React.CSSProperties}
              className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none sm:group-hover:scale-[1.03] transition-transform duration-500 ease-out"
            />
          )}
          {locked && <ModeLockOverlay />}
        </div>
        {/* Texto suelto debajo de la imagen — sin card, sin border, sin
            padding lateral. */}
        <div className="mt-3 flex flex-col gap-0.5">
          <h3 className="text-[15.5px] sm:text-[17px] font-semibold text-brand-black/90 leading-tight transition-colors duration-300 group-hover:text-brand-primary">
            {preset.title}
          </h3>
          {preset.description && (
            <p className="text-[13px] sm:text-[13.5px] text-[#3C3C43]/60 leading-snug">
              {preset.description}
            </p>
          )}
        </div>
      </button>
    );
  }
  const isVideo = preset.outputType === 'video';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`shrink-0 w-[42%] sm:w-[270px] aspect-[3/4] rounded-xl overflow-hidden bg-neutral-100 relative group active:scale-[0.98] transition-transform ${className || ''}`}
    >
      {isVideo ? (
        <video
          ref={setupAutoplayVideo}
          src={preset.imageUrlFull || preset.imageUrl}
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          poster={preset.imageUrl}
          draggable={false}
          onDragStart={(e) => e.preventDefault()}
          style={{ WebkitTouchCallout: "none" } as React.CSSProperties}
          className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none sm:group-hover:scale-[1.03] transition-transform duration-500 ease-out"
        />
      ) : (
        <img
          src={preset.imageUrl}
          alt={preset.title}
          loading="lazy"
          draggable={false}
          onDragStart={(e) => e.preventDefault()}
          style={{ WebkitTouchCallout: "none" } as React.CSSProperties}
          className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none sm:group-hover:scale-[1.03] transition-transform duration-500 ease-out"
        />
      )}
      {isVideo && (
        // Badge play: círculo semitransparente arriba a la derecha,
        // indicando que la card es video y no imagen.
        <span className="absolute top-2 right-2 w-7 h-7 inline-flex items-center justify-center rounded-full bg-black/40 backdrop-blur-sm text-white shadow">
          <Play className="w-3.5 h-3.5 fill-white" strokeWidth={0} />
        </span>
      )}
      {/* Gradient overlay para que el título sea legible sobre cualquier foto. */}
      <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/55 via-black/10 to-transparent pointer-events-none" />
      <p className="absolute bottom-2.5 left-3 right-3 text-left text-white text-[13px] font-medium leading-tight drop-shadow">
        {preset.title}
      </p>
    </button>
  );
};

// ─── Collection row ─────────────────────────────────────────────────────
// Cada colección: título + (subtítulo opcional) + grid horizontal
// scrolleable de preset cards. Si hay más presets que `PRESETS_PREVIEW_COUNT`
// agregamos al final una card de "Ver más" que navega al detalle.
export interface CollectionRowProps {
  collection: CollectionV2;
  onPresetClick: (p: PresetV2) => void;
  onVerMas: () => void;
  /** Opcional: marca una card como bloqueada por plan (candado). Solo se pasa
   *  en el design-row de modos; en las filas de plantillas queda sin usar. */
  isPresetLocked?: (p: PresetV2) => boolean;
}

export const CollectionRow: React.FC<CollectionRowProps> = ({
  collection,
  onPresetClick,
  onVerMas,
  isPresetLocked,
}) => {
  // Renderizamos hasta el count DESKTOP, pero las cards más allá del count
  // MOBILE quedan `hidden md:block` — desktop las ve, mobile no.
  // "Ver más" se muestra/oculta por breakpoint según si el respectivo
  // count se excede.
  const total = collection.presets.length;
  const visible = collection.presets.slice(0, PRESETS_PREVIEW_COUNT_DESKTOP);
  const moreOnMobile = total > PRESETS_PREVIEW_COUNT_MOBILE;
  const moreOnDesktop = total > PRESETS_PREVIEW_COUNT_DESKTOP;
  // Si no hay overflow en NINGÚN device, el link/card no se renderiza.
  // Si hay overflow solo en mobile, se renderiza con `md:hidden` (oculto
  // en desktop). Si hay overflow en desktop también, se renderiza sin
  // ese modifier.
  const anyMore = moreOnMobile || moreOnDesktop;
  const verMasResponsive = !moreOnDesktop ? 'md:hidden' : '';

  // La fila virtual "Diseña tu imagen" no muestra header — las cards
  // ya llevan el nombre del modo en el badge centrado, repetir el
  // título arriba era redundante.
  const hideHeader = collection.id === 'design-row';
  return (
    <section>
      {!hideHeader && (
        <header className="mb-3 flex items-center justify-between gap-3">
          <h2 className="text-[18px] sm:text-[20px] font-semibold text-brand-black/90 leading-tight min-w-0">
            {collection.title}
          </h2>
          {anyMore && (
            <button
              type="button"
              onClick={onVerMas}
              className={`inline-flex items-center gap-0.5 text-[13px] text-brand-primary hover:opacity-75 shrink-0 transition-opacity ${verMasResponsive}`}
            >
              Ver más
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          )}
        </header>
      )}
      <HoverScrollCarousel
        arrowAlignToMedia
        className="-mx-5 sm:-mx-8 px-5 sm:px-8 flex gap-3 overflow-x-auto scrollbar-hide"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {visible.map((preset, i) => (
          <PresetCard
            key={preset.id}
            preset={preset}
            onClick={() => onPresetClick(preset)}
            locked={isPresetLocked ? isPresetLocked(preset) : false}
            // Cards más allá del count mobile se ocultan en mobile.
            className={
              i >= PRESETS_PREVIEW_COUNT_MOBILE ? 'hidden md:block' : ''
            }
          />
        ))}
        {anyMore && (
          <button
            type="button"
            onClick={onVerMas}
            className={`shrink-0 w-[42%] sm:w-[270px] aspect-[3/4] rounded-xl border border-dashed border-neutral-300 bg-white hover:border-brand-primary/40 hover:bg-brand-primary/[0.02] transition-colors flex flex-col items-center justify-center gap-2 text-[#3C3C43]/55 hover:text-brand-primary ${verMasResponsive}`}
          >
            <ChevronRight className="w-5 h-5" />
            <span className="text-[13px] font-medium">Ver toda la colección</span>
          </button>
        )}
      </HoverScrollCarousel>
    </section>
  );
};

// ─── Home skeleton ─────────────────────────────────────────────────────
// Se muestra mientras restauramos scroll después de volver desde una
// sub-ruta (categoría, plantillas). Estructura simplificada que imita
// la home: hero arriba + 2-3 rows de cards horizontales debajo. Las
// cards usan `animate-pulse` con fondo `bg-black/[0.06]` — mismo
// tratamiento que el skeleton de `ColeccionDetalle` y otros pages.
export const HomeSkeleton: React.FC = () => {
  const rowCardWidths = ['w-[42%]', 'w-[42%]', 'w-[42%]', 'w-[42%]'];
  return (
    <div className="max-w-content mx-auto px-5 sm:px-8 pt-4 sm:pt-8 pb-24">
      {/* Hero / greeting */}
      <div className="hidden sm:block max-w-2xl mx-auto mb-12 sm:mb-14 text-center">
        <div className="h-9 sm:h-12 w-3/4 mx-auto rounded-md bg-black/[0.06] animate-pulse" />
        <div className="h-5 w-1/2 mx-auto rounded-md bg-black/[0.06] animate-pulse mt-3" />
      </div>
      {/* Mobile hero: card grande — mismo aspect-[4/5] que el MobileModeHero
          real para que no haya salto de layout al terminar de cargar. */}
      <div className="sm:hidden -mx-5 aspect-[4/5] bg-black/[0.06] animate-pulse" />
      {/* Desktop design-row: dos cards lado a lado */}
      <div className="hidden sm:flex gap-3 mt-10">
        <div className="flex-1 h-[360px] rounded-xl bg-black/[0.06] animate-pulse" />
        <div className="flex-1 h-[360px] rounded-xl bg-black/[0.06] animate-pulse" />
      </div>
      {/* Desktop CTA */}
      <div className="hidden sm:block mt-10 sm:mt-12">
        <div className="h-[100px] rounded-2xl bg-black/[0.06] animate-pulse" />
      </div>
      {/* Rows de categorías — title chico + 4 cards horizontales */}
      <div className="mt-10 sm:mt-14 space-y-10 sm:space-y-12 sm:hidden">
        {Array.from({ length: 3 }).map((_, ri) => (
          <div key={ri}>
            <div className="h-5 w-32 rounded-md bg-black/[0.06] animate-pulse mb-3" />
            <div className="-mx-5 px-5 flex gap-3 overflow-x-hidden">
              {rowCardWidths.map((w, i) => (
                <div
                  key={i}
                  className={`shrink-0 ${w} aspect-[3/4] rounded-xl bg-black/[0.06] animate-pulse`}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
